import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useNotification } from '../contexts/NotificationContext';

// PWA インストールを促す小さなポップアップ（下部・最前面）。
// - beforeinstallprompt が発火したブラウザ（Android Chrome 等）: 「インストール」ボタン
// - iOS Safari 等の非対応ブラウザ（未インストール時）: 手動追加の手順を案内
// - 既にスタンドアロン起動（インストール済み）の場合は表示しない
// - ×ボタンで閉じられ、閉じたら一定期間は再表示しない（localStorage）
//
// レイアウトは 1カラム（縦積み）。狭い画面で文字が潰れないようにする。
const DISMISS_KEY = 'pwa_banner_dismissed_at';
const DISMISS_DAYS = 7;

export default function PWAInstallBanner() {
  const notif = useNotification() || {};
  const { isInstallable, installPWA } = notif;
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    try {
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dismissedAt && (Date.now() - dismissedAt) < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
        return;
      }
    } catch (e) {}

    const ua = window.navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    if (isInstallable || iOS) {
      setVisible(true);
    }
  }, [isInstallable]);

  const handleClose = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (e) {}
  };

  const handleInstall = async () => {
    if (installPWA) {
      const ok = await installPWA();
      if (ok) setVisible(false);
    }
  };

  // 非表示時は DOM に何も描画しない（領域を一切占有しない）
  if (!visible) return null;

  return (
    <Box
      role="dialog"
      aria-label="アプリのインストール案内"
      sx={{
        position: 'fixed',
        bottom: { xs: 72, sm: 16 }, // モバイルは下部ナビと重ならないよう上げる
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 24px)',
        maxWidth: 420,
        zIndex: 2000, // 最前面
        bgcolor: 'background.paper',
        boxShadow: 6,
        borderRadius: 2,
        p: 2,
        // 1カラム縦積み
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* ヘッダー行: アイコン + タイトル + 閉じるボタン */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GetAppIcon color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          アプリとして使えます
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label="閉じる" sx={{ ml: 'auto' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* 説明文（折り返し可・複数行OK） */}
      {isIOS && !isInstallable ? (
        <Typography variant="body2" color="text.secondary">
          Safari の共有ボタン <IosShareIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} /> から
          「ホーム画面に追加」を選ぶと、アプリとしてインストールできます。
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          ホーム画面に追加すると、アプリのように素早く開けてプッシュ通知も受け取れます。
        </Typography>
      )}

      {/* アクションボタン（全幅） */}
      {!isIOS && isInstallable && (
        <Button
          variant="contained"
          fullWidth
          startIcon={<GetAppIcon />}
          onClick={handleInstall}
          sx={{ mt: 0.5 }}
        >
          アプリをインストール
        </Button>
      )}
    </Box>
  );
}
